/**
 * Auth state machine of the office portal (ADR-03 §2.3, ADR-08 §2.1 containers):
 *
 *   loading ──GET /auth/session──► 401 → login (password → second factor | TOTP enrolment)
 *                                 │ state password_ok → login at the pending step
 *                                 └ state active → authenticated shell
 *   /setup/<token> (D-037 / D-132) → first-login setup page, then login.
 *
 * Any 401 inside the shell returns to login; logout = POST /auth/logout + cache reset.
 * Idle (D-039) and working-hours (D-210) warnings are derived from the session payload: the
 * session is re-read on user activity (throttled), never on a timer, so polling cannot keep an
 * idle session alive.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createT } from "@faylasuf/i18n";
import { I18nProvider, LoadingScreen, useT } from "@faylasuf/ui";
import type { SessionInfo } from "../api/types.js";
import { LoginPage } from "../pages/LoginPage.js";
import { SetupPage } from "../pages/SetupPage.js";
import type { Boot } from "./bootstrap.js";
import { AppContext, SessionContext, type AppContextValue, type SessionContextValue } from "./context.js";
import { errorMessage, isSessionLost } from "./errors.js";
import { DEFAULT_PREFS, readViewerPrefs, useDocumentEffects, usePreferences, type UiPrefs } from "./preferences.js";
import { keys, onUnexpectedQueryError } from "./query.js";
import { Shell } from "./Shell.js";
import { ToastProvider, useToast } from "./toast.js";
import { beginScreen } from "./telemetry.js";

const SESSION_REFRESH_THROTTLE_MS = 60_000;

function setupTokenFromPath(pathname: string): string | null {
  const m = /^\/setup\/([A-Za-z0-9_-]{8,160})\/?$/.exec(pathname);
  return m?.[1] ?? null;
}

export function App({ boot }: { boot: Boot }) {
  const qc = useQueryClient();
  const [viewerPrefs, setViewerPrefs] = useState<UiPrefs>(() => ({
    ...DEFAULT_PREFS,
    ...readViewerPrefs(),
    ...(boot.harness.theme ? { theme: boot.harness.theme } : {}),
    ...(boot.harness.lang ? { language: boot.harness.lang } : {}),
  }));
  const [setupToken, setSetupToken] = useState<string | null>(() => setupTokenFromPath(window.location.pathname));

  const sessionQuery = useQuery({
    queryKey: keys.session,
    queryFn: () => boot.source.session(),
    retry: false,
    staleTime: Infinity,
  });
  const session: SessionInfo | undefined = sessionQuery.data;
  const authed = session?.session.state === "active";

  const prefsApi = usePreferences(boot.source, authed, viewerPrefs);
  const prefs = authed ? prefsApi.prefs : viewerPrefs;
  useDocumentEffects(prefs);

  const t = useMemo(() => createT(prefs.language), [prefs.language]);
  const appValue = useMemo<AppContextValue>(() => ({ boot, source: boot.source, now: boot.now, locale: prefs.language, t }), [boot, prefs.language, t]);

  const refreshSession = useCallback(() => qc.invalidateQueries({ queryKey: keys.session }), [qc]);
  const sessionLost = useCallback(() => {
    qc.removeQueries({ predicate: (q) => q.queryKey !== keys.session });
    qc.resetQueries({ queryKey: keys.session });
  }, [qc]);
  const explicitLogout = useRef(false);
  const logout = useCallback(async () => {
    explicitLogout.current = true;
    try {
      await boot.source.logout();
    } finally {
      sessionLost();
    }
  }, [boot.source, sessionLost]);

  // "The session has ended" is shown only when an authenticated shell falls back to login without a logout.
  const wasAuthed = useRef(false);
  const [lostNotice, setLostNotice] = useState(false);
  useEffect(() => {
    if (authed) {
      wasAuthed.current = true;
      setLostNotice(false);
    } else if (wasAuthed.current) {
      wasAuthed.current = false;
      if (!explicitLogout.current) setLostNotice(true);
      explicitLogout.current = false;
    }
  }, [authed]);

  // Activity-driven session refresh: idle (D-039) and hours (D-210) states come from the server payload.
  const lastRefresh = useRef(Date.now());
  useEffect(() => {
    if (!authed) return;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastRefresh.current >= SESSION_REFRESH_THROTTLE_MS) {
        lastRefresh.current = now;
        void refreshSession();
      }
    };
    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown"];
    for (const ev of events) window.addEventListener(ev, onActivity, { passive: true });
    return () => {
      for (const ev of events) window.removeEventListener(ev, onActivity);
    };
  }, [authed, refreshSession]);

  // The viewer's last theme / language survive logout for the login screen (per-viewer convenience only).
  useEffect(() => {
    if (authed) setViewerPrefs((v) => (v.theme === prefs.theme && v.language === prefs.language ? v : { ...v, theme: prefs.theme, language: prefs.language }));
  }, [authed, prefs.theme, prefs.language]);

  const sessionValue = useMemo<SessionContextValue | null>(() => {
    if (!session) return null;
    const perms = new Set(session.permissions);
    return {
      session,
      hasPermission: (token) => perms.has(token),
      isSuperManager: session.user.roleBuiltin === "super_manager",
      refresh: refreshSession,
      sessionLost,
      logout,
    };
  }, [session, refreshSession, sessionLost, logout]);

  // [C28] D-259: the screens outside the shell (the shell begins its own per section)
  const outerScreen = setupToken ? "setup" : sessionQuery.isPending ? "boot" : !session || session.session.state !== "active" ? "login" : null;
  const lastOuter = useRef<string | null>(null);
  if (outerScreen && outerScreen !== lastOuter.current) beginScreen(outerScreen);
  lastOuter.current = outerScreen;

  let content;
  if (setupToken) {
    content = (
      <SetupPage
        token={setupToken}
        onDone={() => {
          window.history.replaceState(null, "", "/" + window.location.search);
          setSetupToken(null);
        }}
      />
    );
  } else if (sessionQuery.isPending) {
    content = <LoadingScreen />;
  } else if (sessionQuery.isError && !isSessionLost(sessionQuery.error)) {
    // Network / server failure before anything is known: the login screen shows the translated error.
    content = <LoginPage initialError={sessionQuery.error} onAuthenticated={refreshSession} />;
  } else if (!session || session.session.state !== "active") {
    content = <LoginPage pendingSession={session ?? null} onAuthenticated={refreshSession} />;
  } else if (!prefsApi.loaded) {
    content = <LoadingScreen />;
  } else {
    content = (
      <SessionContext.Provider value={sessionValue}>
        <Shell prefs={prefs} setPreference={prefsApi.set} />
      </SessionContext.Provider>
    );
  }

  return (
    <AppContext.Provider value={appValue}>
      <I18nProvider locale={prefs.language} t={t}>
        <ToastProvider>
          <SessionLostToast lost={lostNotice} />
          <QueryErrorToasts />
          {content}
        </ToastProvider>
      </I18nProvider>
    </AppContext.Provider>
  );
}

/** Shows "the session has ended" once when an authenticated shell falls back to login (not on the first load). */
function SessionLostToast({ lost }: { lost: boolean }) {
  const toast = useToast();
  const t = useT();
  const shown = useRef(false);
  useEffect(() => {
    if (lost && !shown.current) {
      shown.current = true;
      toast(t("app.session_ended"), "info");
    }
    if (!lost) shown.current = false;
  }, [lost, toast, t]);
  return null;
}

/** [C28] F-272: unexpected read failures as an error toast with the trace reference (once per message per 5 s). */
function QueryErrorToasts() {
  const toast = useToast();
  const t = useT();
  useEffect(() => {
    const last = new Map<string, number>();
    return onUnexpectedQueryError((e) => {
      const msg = errorMessage(t, e);
      const key = msg.replace(/[0-9a-f]{12}$/, "");
      const now = Date.now();
      if ((last.get(key) ?? 0) > now - 5000) return;
      last.set(key, now);
      toast(msg, "error");
    });
  }, [toast, t]);
  return null;
}
